/**
 * DOS518 - Digital Operations System for Wildfire Management
 *
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 */
// ComunicazioniOperativeModal - VERSIONE COMPLETA CON LOG E CALLSIGN DINAMICO
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { toPhonetic, toPhoneticCallsign } from '../../utils/fraseologia';

interface Props { isOpen: boolean; onClose: () => void; }
type Scenario = 'primo_lancio' | 'traffico_sconosciuto' | 'trascrizione_dati' | 'mayday' | 'cambio_dos' | 'promemoria';

export const ComunicazioniOperativeModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const addLogComunicazione = useAppStore(state => state.addLogComunicazione);
  const cambioDOS = useAppStore(state => state.cambioDOS);
  const mezziAerei = useAppStore(state => state.mezziAerei);
  const coau = useAppStore(state => state.coau);

  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [mezzoSelezionatoId, setMezzoSelezionatoId] = useState<string>('');

  // Primo Lancio
  const [zona, setZona] = useState<'libera' | 'occupata' | 'emergenza' | null>(null);

  // Traffico Sconosciuto
  const [tipoTs, setTipoTs] = useState('');
  const [coloreTs, setColoreTs] = useState('');
  const [rottaTs, setRottaTs] = useState('');
  const [msgTsGenerato, setMsgTsGenerato] = useState('');

  // Trascrizione Dati
  const [stepTd, setStepTd] = useState(1);

  // MAYDAY
  const [stepMd, setStepMd] = useState(1);
  const [maydayPosizione, setMaydayPosizione] = useState('');
  const [maydayNatura, setMaydayNatura] = useState('');
  const [maydayPersone, setMaydayPersone] = useState('');
  const [maydayAutonomia, setMaydayAutonomia] = useState('');
  const [maydayIntenzioni, setMaydayIntenzioni] = useState('');
  const [maydayRegistrato, setMaydayRegistrato] = useState(false);

  // Cambio DOS
  const [dosUscente, setDosUscente] = useState('');
  const [dosEntrante, setDosEntrante] = useState('');
  const [cambioDosRegistrato, setCambioDosRegistrato] = useState(false);

  if (!isOpen) return null;

  // Callsign dinamico mezzo
  const mezzoAttivo =
    mezziAerei.find(m => m.id === mezzoSelezionatoId) ||
    mezziAerei.find(m => ['Primo al Lancio', 'In Orbita', 'Ha Lanciato', 'in_avvicinamento'].includes(m.posizioneOperativa)) ||
    mezziAerei[0];
  const callsignMezzo = mezzoAttivo ? toPhoneticCallsign(mezzoAttivo.sigla) : 'MEZZO';
  const siglaMezzo = mezzoAttivo?.sigla || 'MEZZO';

  // Nominativo DOS da COAU → fonetico ICAO
  const nominativoDOS = (coau as any)?.nominativoDOS;
  const callsignDOS = nominativoDOS ? toPhonetic(nominativoDOS) : 'DOS';

  const reset = () => {
    setScenario(null);
    setZona(null);
    setTipoTs(''); setColoreTs(''); setRottaTs(''); setMsgTsGenerato('');
    setStepTd(1);
    setStepMd(1); setMaydayPosizione(''); setMaydayNatura(''); setMaydayPersone('');
    setMaydayAutonomia(''); setMaydayIntenzioni(''); setMaydayRegistrato(false);
    setDosUscente(''); setDosEntrante(''); setCambioDosRegistrato(false);
  };

  // Selezione mezzo (comune a tutti gli scenari)
  const SelettoMezzo = () => (
    mezziAerei.length > 0 ? (
      <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,107,53,0.3)' }}>
        <label style={{ color: '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>✈️ Mezzo Aereo</label>
        <select
          value={mezzoSelezionatoId}
          onChange={e => setMezzoSelezionatoId(e.target.value)}
          style={{ width: '100%', padding: '8px', background: 'rgba(0,0,0,0.3)', border: '2px solid rgba(255,107,53,0.4)', borderRadius: '6px', color: '#fff', fontSize: '13px' }}
        >
          <option value="">— Auto (primo disponibile) —</option>
          {mezziAerei.map(m => (
            <option key={m.id} value={m.id}>
              {m.sigla} — {m.tipo === 'ala_fissa' ? 'Canadair' : 'Elicottero'} ({m.posizioneOperativa})
            </option>
          ))}
        </select>
        {mezzoAttivo && (
          <div style={{ marginTop: '6px', color: '#aaa', fontSize: '12px' }}>
            Fonetico: <em style={{ color: '#ff6b35' }}>{callsignMezzo}</em>
          </div>
        )}
      </div>
    ) : (
      <div style={{ padding: '10px', background: 'rgba(255,193,7,0.1)', border: '1px solid #ffc107', borderRadius: '6px', marginBottom: '15px' }}>
        <p style={{ color: '#ffc107', margin: 0, fontSize: '12px' }}>⚠️ Nessun mezzo registrato in Risorse → Mezzi Aerei</p>
      </div>
    )
  );

  // ============================================================
  // MENU PRINCIPALE
  // ============================================================
  if (!scenario) return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>📡 COMUNICAZIONI OPERATIVE</h3><button className="btn-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ background: 'rgba(255,107,53,0.1)', border: '2px solid #ff6b35', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
            <h4 style={{ color: '#ff6b35', margin: '0 0 8px 0' }}>⚠️ SILENZIO RADIO</h4>
            <p style={{ color: '#fff', margin: 0, fontSize: '14px' }}><strong>30 sec PRIMA</strong> e <strong>30 sec DOPO</strong> il lancio: 🔇 SILENZIO ASSOLUTO</p>
          </div>
          <h4 style={{ color: '#ff6b35', marginBottom: '20px' }}>Seleziona Scenario:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px,1fr))', gap: '15px' }}>
            {[
              { id: 'primo_lancio', l: '🎯 Primo al Lancio' },
              { id: 'traffico_sconosciuto', l: '📡 Traffico Sconosciuto' },
              { id: 'trascrizione_dati', l: '📊 Trascrizione Dati' },
              { id: 'mayday', l: '🚨 MAYDAY' },
              { id: 'cambio_dos', l: '🔄 Cambio DOS' },
              { id: 'promemoria', l: '📝 Promemoria' },
            ].map(s => (
              <button key={s.id} onClick={() => setScenario(s.id as Scenario)}
                style={{ padding: '20px', background: 'rgba(255,255,255,0.05)', border: `3px solid ${s.id === 'mayday' ? 'rgba(231,76,60,0.5)' : 'rgba(255,107,53,0.3)'}`, borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>
                {s.l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // SCENARIO: PRIMO AL LANCIO
  // ============================================================
  if (scenario === 'primo_lancio') {
    const handleSelectZona = (z: 'libera' | 'occupata' | 'emergenza') => {
      setZona(z);
      const msg = z === 'libera'
        ? `${callsignMezzo} ${callsignDOS}, Zona Libera Lancio Approvato`
        : z === 'occupata'
          ? `${callsignMezzo} ${callsignDOS}, ORBITATE`
          : `${callsignMezzo} ${callsignDOS}, ANNULLATE RIPETO ANNULLATE LANCIO`;
      addLogComunicazione({
        tipo: 'operativa',
        mittente: nominativoDOS || 'DOS',
        destinatario: siglaMezzo,
        messaggio: msg,
        priorita: z === 'emergenza' ? 'urgente' : 'normale',
      });
    };

    return (
      <div className="modal-overlay" onClick={reset}>
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><h3>🎯 PRIMO AL LANCIO</h3><button className="btn-close" onClick={reset}>✕</button></div>
          <div className="modal-body">
            <SelettoMezzo />
            <div style={{ background: 'rgba(255,107,53,0.1)', border: '2px solid #ff6b35', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
              <p style={{ color: '#fff', margin: 0, fontSize: '14px' }}>
                🛩️ <em>"{callsignMezzo} {callsignDOS}, un primo al lancio"</em>
              </p>
            </div>
            <h4 style={{ color: '#ff6b35', marginBottom: '15px' }}>Situazione zona lancio?</h4>
            {(['libera', 'occupata', 'emergenza'] as const).map(z => (
              <button key={z} onClick={() => handleSelectZona(z)}
                style={{ width: '100%', padding: '18px', background: zona === z ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.05)', border: `3px solid ${zona === z ? '#2ecc71' : 'rgba(255,255,255,0.1)'}`, borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginBottom: '12px', textAlign: 'left' }}>
                <div style={{ fontWeight: '900', marginBottom: '5px' }}>
                  {z === 'libera' ? '🟢 ZONA LIBERA' : z === 'occupata' ? '🟡 ZONA OCCUPATA' : '🔴 EMERGENZA'}
                </div>
                <div style={{ fontSize: '13px', color: '#aaa' }}>
                  {callsignMezzo} {callsignDOS},{' '}
                  {z === 'libera' ? 'Zona Libera Lancio Approvato' : z === 'occupata' ? 'ORBITATE' : 'ANNULLATE RIPETO ANNULLATE LANCIO'}
                </div>
              </button>
            ))}
            {zona && (
              <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(46,204,113,0.1)', borderRadius: '8px', border: '2px solid #2ecc71' }}>
                <p style={{ color: '#2ecc71', margin: 0, fontWeight: '700' }}>✅ Comunicazione registrata nel Log!</p>
              </div>
            )}
            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button className="btn btn-outline" onClick={reset} style={{ width: '100%' }}>← Torna Scenari</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // SCENARIO: TRAFFICO SCONOSCIUTO
  // ============================================================
  if (scenario === 'traffico_sconosciuto') {
    const tipiAeromobile = ['Elicottero', 'Canadair', 'Aereo', 'Drone', 'Aliante', 'Non identificato'];
    const direzioniRotta = ['da Nord a Sud', 'da Sud a Nord', 'da Est a Ovest', 'da Ovest a Est', 'da Nord-Est a Sud-Ovest', 'da Nord-Ovest a Sud-Est', 'da Sud-Est a Nord-Ovest', 'da Sud-Ovest a Nord-Est'];

    const handleGeneraBreak = () => {
      if (!tipoTs) return;
      const descrizione = [tipoTs, coloreTs ? `colore ${coloreTs}` : '', rottaTs ? `in rotta ${rottaTs}` : ''].filter(Boolean).join(', ');
      const msg = `BREAK BREAK BREAK BREAK BREAK BREAK — Traffico sconosciuto ${descrizione} — Identificarsi o abbandonare la zona operativa — ${callsignDOS}`;
      setMsgTsGenerato(msg);
      addLogComunicazione({
        tipo: 'operativa',
        mittente: nominativoDOS || 'DOS',
        destinatario: 'ALL STATIONS',
        messaggio: msg,
        priorita: 'urgente',
      });
    };

    return (
      <div className="modal-overlay" onClick={reset}>
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><h3>📡 TRAFFICO SCONOSCIUTO</h3><button className="btn-close" onClick={reset}>✕</button></div>
          <div className="modal-body">
            <div style={{ background: 'rgba(255,193,7,0.1)', border: '2px solid #ffc107', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
              <p style={{ color: '#ffc107', margin: 0, fontSize: '13px' }}>⚠️ Aeromobile non identificato nella zona operativa</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Tipo Aeromobile *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {tipiAeromobile.map(t => (
                  <button key={t} onClick={() => setTipoTs(t)}
                    style={{ padding: '8px 14px', background: tipoTs === t ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.05)', border: `2px solid ${tipoTs === t ? '#ff6b35' : 'rgba(255,255,255,0.15)'}`, borderRadius: '6px', color: '#fff', fontSize: '13px', cursor: 'pointer' }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Colore (opzionale)</label>
              <input
                type="text"
                value={coloreTs}
                onChange={e => setColoreTs(e.target.value)}
                placeholder="es. bianco, rosso, giallo..."
                style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.2)', borderRadius: '6px', color: '#fff', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>Rotta (opzionale)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {direzioniRotta.map(d => (
                  <button key={d} onClick={() => setRottaTs(d)}
                    style={{ padding: '8px 14px', background: rottaTs === d ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.05)', border: `2px solid ${rottaTs === d ? '#ff6b35' : 'rgba(255,255,255,0.15)'}`, borderRadius: '6px', color: '#fff', fontSize: '12px', cursor: 'pointer' }}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleGeneraBreak} disabled={!tipoTs}
              style={{ width: '100%', padding: '18px', background: tipoTs ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '16px', fontWeight: '900', cursor: tipoTs ? 'pointer' : 'not-allowed', marginBottom: '15px' }}>
              📡 GENERA BREAK BREAK BREAK
            </button>

            {msgTsGenerato && (
              <>
                <div style={{ background: 'rgba(0,0,0,0.4)', padding: '15px', borderRadius: '8px', border: '2px solid #f39c12', marginBottom: '10px' }}>
                  <p style={{ color: '#fff', margin: 0, fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.6' }}>{msgTsGenerato}</p>
                </div>
                <div style={{ padding: '12px', background: 'rgba(46,204,113,0.1)', borderRadius: '8px', border: '2px solid #2ecc71' }}>
                  <p style={{ color: '#2ecc71', margin: 0, fontSize: '13px', fontWeight: 700 }}>✅ Comunicazione URGENTE registrata nel Log!</p>
                </div>
              </>
            )}

            <div style={{ marginTop: '25px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button className="btn btn-outline" onClick={reset} style={{ width: '100%' }}>← Torna Scenari</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // SCENARIO: TRASCRIZIONE DATI
  // ============================================================
  if (scenario === 'trascrizione_dati') {
    const lat = (coau as any)?.latitudine || '—';
    const lon = (coau as any)?.longitudine || '—';
    const codice = (coau as any)?.codiceIntervento || (coau as any)?.codice_intervento || '—';
    const localita = (coau as any)?.localita || '—';

    const passi = [
      {
        da: 'MEZZO', testo: `${callsignDOS} ${callsignMezzo}, richiedo dati per trascrizione`,
        log: null,
      },
      {
        da: 'DOS', testo: `${callsignMezzo} ${callsignDOS}, procedete, in ascolto`,
        log: { mittente: 'DOS', destinatario: siglaMezzo, messaggio: `${callsignMezzo} ${callsignDOS}, procedete, in ascolto` },
      },
      {
        da: 'DOS', testo: `Codice intervento: ${codice} — Località: ${localita} — Coordinate: ${lat}, ${lon} — Frequenza operativa invariata`,
        log: { mittente: 'DOS', destinatario: siglaMezzo, messaggio: `Dati trasmessi: codice ${codice}, località ${localita}, coordinate ${lat} ${lon}` },
      },
      {
        da: 'MEZZO', testo: `${callsignDOS} ${callsignMezzo}, dati copiati, ripeto: codice ${codice}, località ${localita}, coordinate ${lat}, ${lon}`,
        log: null,
      },
      {
        da: 'DOS', testo: `${callsignMezzo} ${callsignDOS}, confermo, dati corretti. Potete procedere`,
        log: { mittente: 'DOS', destinatario: siglaMezzo, messaggio: `${callsignMezzo} ${callsignDOS}, dati confermati, procedere` },
      },
    ];

    const handleAvanza = () => {
      const passo = passi[stepTd - 1];
      if (passo.log) {
        addLogComunicazione({
          tipo: 'operativa',
          mittente: passo.log.mittente === 'DOS' ? (nominativoDOS || 'DOS') : passo.log.mittente,
          destinatario: passo.log.destinatario,
          messaggio: passo.log.messaggio,
          priorita: 'normale',
        });
      }
      if (stepTd < passi.length) setStepTd(s => s + 1);
    };

    return (
      <div className="modal-overlay" onClick={reset}>
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><h3>📊 TRASCRIZIONE DATI</h3><button className="btn-close" onClick={reset}>✕</button></div>
          <div className="modal-body">
            <SelettoMezzo />

            {/* Barra avanzamento */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
              {passi.map((_, i) => (
                <div key={i} style={{ flex: 1, height: '6px', borderRadius: '3px', background: i < stepTd ? '#ff6b35' : 'rgba(255,255,255,0.15)' }} />
              ))}
            </div>

            {passi.slice(0, stepTd).map((passo, i) => (
              <div key={i} className={`radio-message ${passo.da === 'DOS' ? 'dos' : 'aereo'}`} style={{ marginBottom: '12px' }}>
                <div className="message-label">{passo.da === 'DOS' ? '📡 DOS' : '🛩️ AEROMOBILE'}</div>
                <div className="message-text"><em>"{passo.testo}"</em></div>
              </div>
            ))}

            {stepTd <= passi.length ? (
              <button onClick={handleAvanza}
                style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '15px' }}>
                {stepTd === passi.length ? '✅ Conferma e Chiudi' : '▶ Passo Successivo'}
              </button>
            ) : (
              <div style={{ padding: '12px', background: 'rgba(46,204,113,0.1)', borderRadius: '8px', border: '2px solid #2ecc71', marginTop: '15px' }}>
                <p style={{ color: '#2ecc71', margin: 0, fontWeight: '700' }}>✅ Trascrizione completata e registrata nel Log!</p>
              </div>
            )}

            <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button className="btn btn-outline" onClick={reset} style={{ width: '100%' }}>← Torna Scenari</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // SCENARIO: MAYDAY
  // ============================================================
  if (scenario === 'mayday') {
    const handleRegistraMayday = () => {
      if (!maydayPosizione || !maydayNatura) return;
      const msg = `MAYDAY MAYDAY MAYDAY — ${siglaMezzo} — Posizione: ${maydayPosizione} — Natura: ${maydayNatura} — Persone: ${maydayPersone || 'n.d.'} — Autonomia: ${maydayAutonomia || 'n.d.'} — Intenzioni: ${maydayIntenzioni || 'n.d.'}`;
      addLogComunicazione({
        tipo: 'operativa',
        mittente: siglaMezzo || 'MEZZO',
        destinatario: 'ALL',
        messaggio: msg,
        priorita: 'mayday',
      });
      setMaydayRegistrato(true);
      setStepMd(3);
    };

    return (
      <div className="modal-overlay" onClick={reset}>
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' }}>
            <h3>🚨 MAYDAY</h3><button className="btn-close" onClick={reset}>✕</button>
          </div>
          <div className="modal-body">
            <SelettoMezzo />

            {/* Barra step */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
              {[1, 2, 3].map(s => (
                <div key={s} style={{ flex: 1, height: '6px', borderRadius: '3px', background: s <= stepMd ? '#e74c3c' : 'rgba(255,255,255,0.15)' }} />
              ))}
            </div>

            {/* STEP 1: Ricezione MAYDAY */}
            {stepMd >= 1 && (
              <div style={{ background: 'rgba(231,76,60,0.15)', border: '2px solid #e74c3c', borderRadius: '10px', padding: '15px', marginBottom: '15px' }}>
                <h4 style={{ color: '#e74c3c', margin: '0 0 8px 0' }}>Step 1 — Ricezione MAYDAY</h4>
                <div className="radio-message aereo" style={{ marginBottom: '10px' }}>
                  <div className="message-label">🛩️ AEROMOBILE</div>
                  <div className="message-text"><strong>MAYDAY MAYDAY MAYDAY</strong></div>
                </div>
                <div className="radio-message dos">
                  <div className="message-label">📡 DOS</div>
                  <div className="message-text"><em>"Stazione in MAYDAY, {callsignDOS} vi riceve. Procedete con 5 elementi"</em></div>
                </div>
                {stepMd === 1 && (
                  <button onClick={() => setStepMd(2)}
                    style={{ width: '100%', marginTop: '12px', padding: '12px', background: '#e74c3c', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>
                    ▶ Ricevuto — Raccogliere i 5 elementi
                  </button>
                )}
              </div>
            )}

            {/* STEP 2: Raccolta 5 elementi */}
            {stepMd >= 2 && !maydayRegistrato && (
              <div style={{ background: 'rgba(231,76,60,0.1)', border: '2px solid rgba(231,76,60,0.5)', borderRadius: '10px', padding: '15px', marginBottom: '15px' }}>
                <h4 style={{ color: '#e74c3c', margin: '0 0 12px 0' }}>Step 2 — Raccolta 5 Elementi Obbligatori</h4>
                {[
                  { label: 'P — POSIZIONE', value: maydayPosizione, set: setMaydayPosizione, required: true, placeholder: 'es. coordinate GPS, riferimento geografico...' },
                  { label: 'N — NATURA emergenza', value: maydayNatura, set: setMaydayNatura, required: true, placeholder: 'es. avaria motore, incendio a bordo...' },
                  { label: 'PER — PERSONE a bordo', value: maydayPersone, set: setMaydayPersone, required: false, placeholder: 'numero persone a bordo' },
                  { label: 'A — AUTONOMIA', value: maydayAutonomia, set: setMaydayAutonomia, required: false, placeholder: 'es. 20 minuti di carburante' },
                  { label: 'I — INTENZIONI', value: maydayIntenzioni, set: setMaydayIntenzioni, required: false, placeholder: 'es. atterraggio emergenza, rientro base...' },
                ].map(({ label, value, set, required, placeholder }) => (
                  <div key={label} style={{ marginBottom: '12px' }}>
                    <label style={{ color: required ? '#e74c3c' : '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                      {label}{required ? ' *' : ''}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={e => set(e.target.value)}
                      placeholder={placeholder}
                      style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.08)', border: `2px solid ${value ? '#2ecc71' : 'rgba(231,76,60,0.4)'}`, borderRadius: '6px', color: '#fff', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
                <button
                  onClick={handleRegistraMayday}
                  disabled={!maydayPosizione || !maydayNatura}
                  style={{ width: '100%', padding: '15px', background: (!maydayPosizione || !maydayNatura) ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '15px', fontWeight: '900', cursor: (!maydayPosizione || !maydayNatura) ? 'not-allowed' : 'pointer', marginTop: '5px' }}>
                  🚨 REGISTRA MAYDAY NEL LOG
                </button>
              </div>
            )}

            {/* STEP 3: Post-MAYDAY */}
            {stepMd >= 3 && maydayRegistrato && (
              <div style={{ background: 'rgba(231,76,60,0.15)', border: '3px solid #e74c3c', borderRadius: '10px', padding: '15px' }}>
                <h4 style={{ color: '#e74c3c', margin: '0 0 10px 0' }}>Step 3 — Attivare i Soccorsi</h4>
                <div style={{ padding: '12px', background: 'rgba(46,204,113,0.1)', borderRadius: '8px', border: '2px solid #2ecc71', marginBottom: '12px' }}>
                  <p style={{ color: '#2ecc71', margin: 0, fontWeight: '700' }}>✅ MAYDAY registrato nel Log con tutti i 5 elementi!</p>
                </div>
                <ul style={{ color: '#fff', fontSize: '14px', paddingLeft: '20px', lineHeight: '1.8' }}>
                  <li>Avvisare immediatamente la <strong>Sala Operativa</strong></li>
                  <li>Comunicare la posizione agli altri mezzi in zona</li>
                  <li>Mantenere il canale radio aperto</li>
                  <li>Non occupare la frequenza se non necessario</li>
                  <li>Attivare protocollo soccorso aereo se necessario</li>
                </ul>
              </div>
            )}

            <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button className="btn btn-outline" onClick={reset} style={{ width: '100%' }}>← Torna Scenari</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // SCENARIO: CAMBIO DOS
  // ============================================================
  if (scenario === 'cambio_dos') {
    const handleCambioDOS = () => {
      if (!dosEntrante.trim()) return;
      const msg = `Attenzione tutte le unità — Cambio DOS in corso — DOS uscente: ${dosUscente || 'in carica'} — DOS entrante: ${dosEntrante} — Frequenza operativa invariata`;
      addLogComunicazione({
        tipo: 'operativa',
        mittente: nominativoDOS || 'DOS',
        destinatario: 'ALL STATIONS',
        messaggio: msg,
        priorita: 'normale',
      });
      cambioDOS(dosEntrante, `Cambio DOS: ${dosUscente || 'in carica'} → ${dosEntrante}`);
      setCambioDosRegistrato(true);
    };

    return (
      <div className="modal-overlay" onClick={reset}>
        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
          <div className="modal-header"><h3>🔄 CAMBIO DOS</h3><button className="btn-close" onClick={reset}>✕</button></div>
          <div className="modal-body">
            {!cambioDosRegistrato ? (
              <>
                <div style={{ background: 'rgba(255,107,53,0.1)', border: '2px solid #ff6b35', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
                  <p style={{ color: '#fff', margin: 0, fontSize: '13px' }}>
                    Il cambio DOS deve essere comunicato a <strong>tutte le unità</strong> prima di essere effettivo.
                  </p>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>DOS Uscente</label>
                  <input
                    type="text"
                    value={dosUscente}
                    onChange={e => setDosUscente(e.target.value)}
                    placeholder="Nome e cognome DOS uscente"
                    style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: '#ff6b35', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '6px' }}>DOS Entrante *</label>
                  <input
                    type="text"
                    value={dosEntrante}
                    onChange={e => setDosEntrante(e.target.value)}
                    placeholder="Nome e cognome DOS entrante"
                    style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.1)', border: `2px solid ${dosEntrante ? '#2ecc71' : 'rgba(255,255,255,0.2)'}`, borderRadius: '8px', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                {dosEntrante && (
                  <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', border: '1px solid rgba(255,107,53,0.3)', marginBottom: '20px' }}>
                    <p style={{ color: '#aaa', fontSize: '12px', margin: '0 0 6px 0' }}>Messaggio che verrà trasmesso:</p>
                    <p style={{ color: '#fff', margin: 0, fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.6' }}>
                      "Attenzione tutte le unità — Cambio DOS in corso — DOS uscente: {dosUscente || 'in carica'} — DOS entrante: {dosEntrante} — Frequenza operativa invariata"
                    </p>
                  </div>
                )}

                <button onClick={handleCambioDOS} disabled={!dosEntrante.trim()}
                  style={{ width: '100%', padding: '18px', background: dosEntrante ? 'linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '16px', fontWeight: '900', cursor: dosEntrante ? 'pointer' : 'not-allowed' }}>
                  🔄 ESEGUI CAMBIO DOS
                </button>
              </>
            ) : (
              <div>
                <div style={{ padding: '20px', background: 'rgba(46,204,113,0.1)', borderRadius: '10px', border: '2px solid #2ecc71', marginBottom: '20px' }}>
                  <p style={{ color: '#2ecc71', margin: 0, fontWeight: '700', fontSize: '16px' }}>✅ Cambio DOS eseguito e registrato!</p>
                  <p style={{ color: '#fff', margin: '10px 0 0 0', fontSize: '14px' }}>
                    <strong>DOS attivo:</strong> {dosEntrante}
                  </p>
                </div>
                <ul style={{ color: '#fff', fontSize: '14px', paddingLeft: '20px', lineHeight: '1.8' }}>
                  <li>Briefing completo con il DOS entrante</li>
                  <li>Trasmettere situazione incendio, risorse, LACES</li>
                  <li>Confermare cambio alla Sala Operativa</li>
                </ul>
              </div>
            )}

            <div style={{ marginTop: '25px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button className="btn btn-outline" onClick={reset} style={{ width: '100%' }}>← Torna Scenari</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // SCENARIO: PROMEMORIA
  // ============================================================
  if (scenario === 'promemoria') return (
    <div className="modal-overlay" onClick={reset}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>📝 PROMEMORIA COMUNICAZIONI</h3><button className="btn-close" onClick={reset}>✕</button></div>
        <div className="modal-body">
          {[
            { titolo: '🔇 Silenzio Radio', testo: '30 secondi PRIMA e 30 secondi DOPO ogni lancio. Nessuna trasmissione durante la manovra.' },
            { titolo: '🎯 Primo al Lancio', testo: 'Il mezzo chiede "un primo al lancio". DOS risponde: ZONA LIBERA, ORBITATE o ANNULLATE LANCIO.' },
            { titolo: '📊 Trascrizione Dati', testo: 'Il mezzo chiede i dati. DOS trasmette: codice intervento, località, coordinate. Il mezzo rilegge per conferma.' },
            { titolo: '📡 Qualità Radio (RST)', testo: '1/5 = incomprensibile, 3/5 = difficoltà, 5/5 = perfetto. Se < 3/5, richiedere ripetizione o cambiare frequenza.' },
            { titolo: '🚨 MAYDAY', testo: 'Raccogliere sempre i 5 elementi: P (posizione), N (natura), PER (persone), A (autonomia), I (intenzioni).' },
            { titolo: '🔄 Cambio DOS', testo: 'Annunciare a tutte le unità prima di cedere il comando. Briefing completo con il subentrante.' },
            { titolo: '📡 BREAK BREAK BREAK', testo: 'Per segnalare traffico non identificato nella zona. Ripetere 3 volte, descrivere tipo/colore/rotta, chiedere identificazione.' },
          ].map(({ titolo, testo }) => (
            <div key={titolo} style={{ padding: '14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,107,53,0.2)', borderRadius: '8px', marginBottom: '12px' }}>
              <h5 style={{ color: '#ff6b35', margin: '0 0 6px 0', fontSize: '14px' }}>{titolo}</h5>
              <p style={{ color: '#ccc', margin: 0, fontSize: '13px', lineHeight: '1.6' }}>{testo}</p>
            </div>
          ))}
          <div style={{ marginTop: '20px' }}>
            <button className="btn btn-outline" onClick={reset} style={{ width: '100%' }}>← Torna Scenari</button>
          </div>
        </div>
      </div>
    </div>
  );

  return null;
};

export default ComunicazioniOperativeModal;
